import React from 'react';
import { isEmpty } from 'lodash';

import { DocumentTemplatesProvider } from '~/components/AiChat/hooks/useActionDocumentTemplate';
import { GeneralActionInner } from '~/components/AiChat/SideBarDialog/Actions/Action/GeneralActionInner';
import { ActionPreview } from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/ActionPreview';
import { ReviewSection } from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/ReviewSection';
import {
  FULL_SCREEN_PREVIEW_CLASS_NAME,
  FullScreenToggleButton,
  useFullScreenToggle,
} from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/useFullScreenToggle';
import type { AiAction } from '~/components/AiChat/types';
import { isDynamicClaimActionTaskData } from '~/components/AiChat/types';
import CardDialog from '~/components/CardDialog';
import PayloadList from '~/components/ClaimPage/CliveApp/ConductorHub/PayloadList';
import { payloadEntries } from '~/components/ClaimPage/CliveApp/ConductorHub/PayloadList/utils';
import { DIALOG_FOOTER_CLASS_NAME } from '~/components/Clive/CliveDialog/constants';
import { FsButton } from '~/components/core';
import Text from '~/components/core/TextComponents/Text';

export interface SuggestedActionReviewDialogProps {
  action: AiAction;
  /** The action's name, as its card titles it — never the task type's key. */
  title: string;
  /** The exposure, where there is one: the card names it under its title, so the dialog does too. */
  subtitle?: string;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Review before execute, for every claim action that runs through the configured-execution path:
 * why Clive recommends it, and, for a dynamic claim action, the fields it will write — the same list
 * the Conductor Hub's cards show, so the handler approves in the dialog exactly what the card said.
 * A legacy per-type action's flat `task_data` is not what it writes (invoice analysis carries ids
 * and a file path there), so it gets no list. A tool with a previewer (`ACTION_PREVIEWERS`) also shows
 * what approval will produce, such as the document a template card will file.
 */
export const SuggestedActionReviewDialog: React.FC<SuggestedActionReviewDialogProps> = ({
  action,
  title,
  subtitle,
  onConfirm,
  onClose,
}) => {
  const { isFullScreen, toggleFullScreen } = useFullScreenToggle();

  return (
    <CardDialog
      title={title}
      subheader={
        subtitle ? (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {subtitle}
          </Text>
        ) : null
      }
      isDialog
      onClose={onClose}
      action={<FullScreenToggleButton isFullScreen={isFullScreen} onClick={toggleFullScreen} />}
      maxWidth="sm"
      fullWidth
      fullScreen={isFullScreen}
      footerClassName={DIALOG_FOOTER_CLASS_NAME}
      footerActions={
        <div className="flex w-full justify-end gap-12">
          <FsButton variant="text" className="text-blue-600" onClick={onClose}>
            Cancel
          </FsButton>
          <FsButton variant="contained" className="bg-green-700 text-white" onClick={onConfirm}>
            Confirm &amp; Execute
          </FsButton>
        </div>
      }
    >
      {/* The preview and the list name the same template, from one read of the claim's templates. */}
      <DocumentTemplatesProvider>
        <div className="flex flex-col gap-16 pb-8">
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            Review what Clive recommends before executing it.
          </Text>

          {/* The conductor writes an empty explanation where it had none; an empty section is noise. */}
          {action.task_data?.explanation ? (
            <ReviewSection heading="Why Clive recommends this">
              <GeneralActionInner action={action} displayTitle={false} />
            </ReviewSection>
          ) : null}

          <ActionPreview action={action} previewClassName={isFullScreen ? FULL_SCREEN_PREVIEW_CLASS_NAME : undefined} />

          {/* The list carries its own heading, so the section adds only the frame. */}
          {isDynamicClaimActionTaskData(action.task_data) && !isEmpty(payloadEntries(action)) ? (
            <ReviewSection>
              <PayloadList action={action} state="pending" />
            </ReviewSection>
          ) : null}
        </div>
      </DocumentTemplatesProvider>
    </CardDialog>
  );
};
